import { snippet } from '@codemirror/autocomplete';
import { indentService } from '@codemirror/language';

/* eslint-disable no-template-curly-in-string -- CodeMirror snippet field placeholders use ${} */

// Liquid tag keywords (used inside {% ... %})
const LIQUID_TAGS = [
  // Control flow
  {
    label: 'if',
    detail: '{% if condition %}',
    apply: snippet('if ${} %}\n  ${}\n{% endif %}'),
    info: 'Conditional block',
  },
  {
    label: 'unless',
    detail: '{% unless condition %}',
    apply: snippet('unless ${} %}\n  ${}\n{% endunless %}'),
    info: 'Inverted conditional',
  },
  {
    label: 'elsif',
    detail: '{% elsif condition %}',
    apply: snippet('elsif ${} %}'),
    info: 'Else-if branch',
  },
  {
    label: 'else',
    detail: '{% else %}',
    apply: 'else %}',
    info: 'Else branch',
  },
  {
    label: 'endif',
    detail: '{% endif %}',
    apply: 'endif %}',
    info: 'End if block',
  },
  {
    label: 'endunless',
    detail: '{% endunless %}',
    apply: 'endunless %}',
    info: 'End unless block',
  },
  // Iteration
  {
    label: 'for',
    detail: '{% for item in collection %}',
    apply: snippet('for ${item} in ${collection} %}\n  ${}\n{% endfor %}'),
    info: 'Loop over a collection',
  },
  {
    label: 'endfor',
    detail: '{% endfor %}',
    apply: 'endfor %}',
    info: 'End for loop',
  },
  {
    label: 'break',
    detail: '{% break %}',
    apply: 'break %}',
    info: 'Exit the loop',
  },
  {
    label: 'continue',
    detail: '{% continue %}',
    apply: 'continue %}',
    info: 'Skip to next iteration',
  },
  // Variable / output
  {
    label: 'assign',
    detail: '{% assign var = value %}',
    apply: snippet('assign ${name} = ${value} %}'),
    info: 'Assign a variable',
  },
  {
    label: 'capture',
    detail: '{% capture var %}',
    apply: snippet('capture ${name} %}\n  ${}\n{% endcapture %}'),
    info: 'Capture output to a variable',
  },
  {
    label: 'endcapture',
    detail: '{% endcapture %}',
    apply: 'endcapture %}',
    info: 'End capture block',
  },
  // Case
  {
    label: 'case',
    detail: '{% case variable %}',
    apply: snippet(
      'case ${variable} %}\n  {% when ${value} %}\n    ${}\n{% endcase %}'
    ),
    info: 'Case/when switch',
  },
  {
    label: 'when',
    detail: '{% when value %}',
    apply: snippet('when ${value} %}'),
    info: 'Case branch',
  },
  {
    label: 'endcase',
    detail: '{% endcase %}',
    apply: 'endcase %}',
    info: 'End case block',
  },
  // Misc
  {
    label: 'comment',
    detail: '{% comment %}',
    apply: snippet('comment %}\n  ${}\n{% endcomment %}'),
    info: 'Comment block',
  },
  {
    label: 'endcomment',
    detail: '{% endcomment %}',
    apply: 'endcomment %}',
    info: 'End comment block',
  },
  {
    label: 'raw',
    detail: '{% raw %}',
    apply: snippet('raw %}\n  ${}\n{% endraw %}'),
    info: 'Raw/verbatim block',
  },
  {
    label: 'endraw',
    detail: '{% endraw %}',
    apply: 'endraw %}',
    info: 'End raw block',
  },
  {
    label: 'cycle',
    detail: '{% cycle "val1", "val2" %}',
    apply: "cycle 'odd', 'even' %}",
    info: 'Cycle through values in a loop',
  },
  {
    label: 'increment',
    detail: '{% increment variable %}',
    apply: snippet('increment ${variable} %}'),
    info: 'Increment a counter',
  },
  {
    label: 'decrement',
    detail: '{% decrement variable %}',
    apply: snippet('decrement ${variable} %}'),
    info: 'Decrement a counter',
  },
];

// Event variables accessible in templates (sourced from EventDrop)
const CONDITION_VARIABLES = [
  { label: 'event.name', info: 'Event name', varType: 'string' },
  { label: 'event.description', info: 'Event description', varType: 'string' },
  {
    label: 'event.start_date',
    info: 'Earliest allocation start date',
    varType: 'date',
  },
  {
    label: 'event.end_date',
    info: 'Latest allocation end date',
    varType: 'date',
  },
  {
    label: 'event.start_time',
    info: 'Earliest allocation start time',
    varType: 'date',
  },
  {
    label: 'event.end_time',
    info: 'Latest allocation end time',
    varType: 'date',
  },
  { label: 'event.pax', info: 'Number of guests (pax)', varType: 'number' },
  { label: 'event.system_stage', info: 'System stage', varType: 'enum' },
  {
    label: 'event.custom_status',
    info: 'Custom status label',
    varType: 'string',
  },
  {
    label: 'event.cover_url',
    info: 'Cover image URL',
    varType: 'string',
  },
  {
    label: 'event.space_names',
    info: 'Allocated space names',
    varType: 'string',
  },
  {
    label: 'event.quote_url',
    info: 'External quote URL',
    varType: 'string',
  },
  {
    label: 'event.customer',
    info: 'Customer contact object',
    varType: 'object',
  },
  {
    label: 'event.customer.name',
    info: 'Customer full name',
    varType: 'string',
  },
  { label: 'event.customer.email', info: 'Customer email', varType: 'string' },
  {
    label: 'event.event_manager',
    info: 'Assigned event manager',
    varType: 'object',
  },
  {
    label: 'event.event_manager.name',
    info: 'Event manager name',
    varType: 'string',
  },
  {
    label: 'event.event_manager.email',
    info: 'Event manager email',
    varType: 'string',
  },
  {
    label: 'event.event_manager.avatar_url',
    info: 'Event manager avatar URL',
    varType: 'string',
  },
  {
    label: 'event.event_manager.phone',
    info: 'Event manager phone',
    varType: 'string',
  },
  {
    label: 'event.pricing',
    info: 'Live pricing sheet breakdown',
    varType: 'object',
  },
  { label: 'event.pricing.total', info: 'Total price (incl. tax)', varType: 'number' },
  { label: 'event.pricing.currency', info: 'Currency code', varType: 'string' },
  {
    label: 'event.quotes',
    info: 'Issued quotes (sent, viewed, accepted)',
    varType: 'array',
  },
  {
    label: 'event.active_quote',
    info: 'Most recent open quote (sent or viewed)',
    varType: 'object',
  },
  {
    label: 'event.active_quote.name',
    info: 'Active quote name',
    varType: 'string',
  },
  {
    label: 'event.active_quote.url',
    info: 'Active quote public signing URL',
    varType: 'string',
  },
  {
    label: 'event.active_quote.signable',
    info: 'Whether the active quote can still be signed',
    varType: 'boolean',
  },
  {
    label: 'event.allocations',
    info: 'Array of allocations',
    varType: 'array',
  },
  {
    label: 'event.floorplans',
    info: 'Array of attached floorplans',
    varType: 'array',
  },
  {
    label: 'event.documents',
    info: 'Array of attached documents',
    varType: 'array',
  },
  {
    label: 'event.attributes',
    info: 'Custom attributes hash',
    varType: 'object',
  },
  {
    label: 'event.attributes_by_category',
    info: 'Attributes grouped by category',
    varType: 'object',
  },
];

// Output variables (superset — also includes share_token etc.)
const EVENT_VARIABLES = [
  ...CONDITION_VARIABLES,
  { label: 'event.share_token', info: 'Public share token' },
  { label: 'event.pricing.subtotal', info: 'Subtotal (excl. tax)' },
  { label: 'event.pricing.tax', info: 'Tax amount' },
  { label: 'event.pricing.tax_label', info: 'Tax label with rate when uniform' },
  { label: 'event.pricing.uniform_tax_rate', info: 'Shared tax rate, or blank if mixed' },
  { label: 'event.pricing.show_line_tax', info: 'True when line items have mixed tax rates' },
  { label: 'event.pricing.line_items', info: 'Array of price line items' },
  { label: 'event.quotes', info: 'Issued quotes for the event' },
  { label: 'event.active_quote', info: 'Most recent open quote' },
  { label: 'event.active_quote.url', info: 'Public URL to view/sign the quote' },
  { label: 'event.active_quote.pricing', info: 'Active quote breakdown' },
];

const SYSTEM_STAGE_VALUES = [
  'draft',
  'inquiry',
  'confirmed',
  'completed',
  'lost',
  'archived',
];

// Suffix operators shown after a complete variable path (before the closing %})
const CONDITION_SUFFIXES = [
  { label: '!= blank', info: 'Is present / not empty' },
  { label: '== blank', info: 'Is absent / empty' },
  { label: '!= nil', info: 'Is not nil' },
  { label: '== nil', info: 'Is nil' },
  { label: '.size > 0', info: 'Array or string has items' },
  { label: '== ', info: 'Equals comparison' },
  { label: '!= ', info: 'Not-equals comparison' },
  { label: 'contains ', info: 'String contains value' },
];

// Liquid filters (appended after | inside {{ }} or {% assign %})
const LIQUID_FILTERS = [
  { label: 'date', detail: '| date: "%B %d, %Y"', info: 'Format a date' },
  { label: 'upcase', info: 'Convert to uppercase' },
  { label: 'downcase', info: 'Convert to lowercase' },
  { label: 'capitalize', info: 'Capitalize first word' },
  { label: 'strip', info: 'Remove leading/trailing whitespace' },
  { label: 'strip_html', info: 'Remove HTML tags' },
  { label: 'escape', info: 'HTML-escape the string' },
  {
    label: 'truncate',
    detail: '| truncate: 50',
    info: 'Truncate to N characters',
  },
  {
    label: 'truncatewords',
    detail: '| truncatewords: 10',
    info: 'Truncate to N words',
  },
  { label: 'split', detail: "| split: ','", info: 'Split string into array' },
  { label: 'join', detail: "| join: ', '", info: 'Join array into string' },
  { label: 'size', info: 'Length of string or array' },
  { label: 'first', info: 'First element of array' },
  { label: 'last', info: 'Last element of array' },
  { label: 'reverse', info: 'Reverse array or string' },
  { label: 'sort', info: 'Sort array' },
  {
    label: 'map',
    detail: "| map: 'attribute'",
    info: 'Map array by attribute',
  },
  {
    label: 'where',
    detail: "| where: 'key', 'value'",
    info: 'Filter array by attribute',
  },
  { label: 'compact', info: 'Remove nil values from array' },
  { label: 'uniq', info: 'Remove duplicate values' },
  { label: 'abs', info: 'Absolute value' },
  { label: 'ceil', info: 'Round up' },
  { label: 'floor', info: 'Round down' },
  { label: 'round', detail: '| round: 2', info: 'Round to N decimal places' },
  { label: 'plus', detail: '| plus: 1', info: 'Add a number' },
  { label: 'minus', detail: '| minus: 1', info: 'Subtract a number' },
  { label: 'times', detail: '| times: 2', info: 'Multiply' },
  { label: 'divided_by', detail: '| divided_by: 2', info: 'Divide' },
  { label: 'modulo', detail: '| modulo: 2', info: 'Modulo' },
  {
    label: 'default',
    detail: "| default: 'fallback'",
    info: 'Fallback if nil/empty/false',
  },
  { label: 'prepend', detail: "| prepend: 'prefix'", info: 'Prepend a string' },
  { label: 'append', detail: "| append: 'suffix'", info: 'Append a string' },
  {
    label: 'replace',
    detail: "| replace: 'a', 'b'",
    info: 'Replace all occurrences',
  },
  {
    label: 'remove',
    detail: "| remove: 'text'",
    info: 'Remove all occurrences',
  },
  {
    label: 'slice',
    detail: '| slice: 0, 3',
    info: 'Extract a substring or sub-array',
  },
];

const CONDITION_KEYWORDS = /^(if|unless|elsif)\s+/;

const LIQUID_OPEN_RE =
  /^\s*\{%-?\s*(if|unless|for|capture|case|comment|raw|else|elsif|when)\b/;
const LIQUID_DEDENT_RE =
  /^\s*\{%-?\s*(endif|endunless|endfor|endcapture|endcase|endcomment|endraw|else|elsif|when)\b/;

/**
 * Analyses a condition expression (the text after `if`/`unless`/`elsif` up to the cursor)
 * and returns what kind of sub-completion to offer, plus the absolute document `from` position.
 *
 * Sub-types:
 *   'value'    – after an operator, offer enum / quoted-string values
 *   'suffix'   – after a complete variable path + space, offer operator suffixes
 *   'variable' – default, offer variable paths
 */
const getConditionSubContext = (conditionExpr, conditionExprDocFrom) => {
  // After a comparison operator + optional partial value → value completions
  // e.g. "event.system_stage == " or "event.system_stage == \"dr"
  const afterOperator = conditionExpr.match(
    /(\S+)\s+(?:==|!=|contains|<|>|<=|>=)\s+(\S*)$/
  );
  if (afterOperator) {
    const varName = afterOperator[1];
    const partial = afterOperator[2];
    const varDef = CONDITION_VARIABLES.find(v => v.label === varName);
    const from = conditionExprDocFrom + conditionExpr.length - partial.length;
    return { sub: 'value', varDef, from };
  }

  // After a complete variable path + whitespace (no operator yet) → suffix completions
  const afterVar = conditionExpr.match(/(\S+)\s+$/);
  if (afterVar) {
    const varName = afterVar[1];
    if (varName.startsWith('event') || varName.startsWith('account')) {
      return {
        sub: 'suffix',
        from: conditionExprDocFrom + conditionExpr.length,
      };
    }
  }

  // Default: completing a variable path
  const partial = conditionExpr.match(/(\S*)$/)?.[1] ?? '';
  return {
    sub: 'variable',
    from: conditionExprDocFrom + conditionExpr.length - partial.length,
  };
};

/**
 * Looks back from `pos` for the nearest `{%` or `{{` that hasn't been closed.
 * Returns { type, from, ... } or null.
 */
const detectLiquidContext = (doc, pos) => {
  const lookback = Math.max(0, pos - 300);
  const text = doc.sliceString(lookback, pos);

  // --- Filter context: pipe inside an open liquid block ---
  const pipeIdx = text.lastIndexOf('|');
  if (pipeIdx !== -1) {
    const tagOpen = text.lastIndexOf('{%');
    const outputOpen = text.lastIndexOf('{{');
    const tagClose = text.lastIndexOf('%}');
    const outputClose = text.lastIndexOf('}}');
    const openIdx = Math.max(tagOpen, outputOpen);
    const closeIdx = Math.max(tagClose, outputClose);
    if (openIdx !== -1 && openIdx > closeIdx && pipeIdx > openIdx) {
      const afterPipe = text.slice(pipeIdx + 1);
      const word = afterPipe.trimStart();
      const from = lookback + pipeIdx + 1 + (afterPipe.length - word.length);
      return { type: 'filter', from };
    }
  }

  // --- Tag context: {% ... %} ---
  const tagOpen = text.lastIndexOf('{%');
  const tagClose = text.lastIndexOf('%}');
  if (tagOpen !== -1 && tagOpen > tagClose) {
    const afterBrace = text.slice(tagOpen + 2);
    const inner = afterBrace.trimStart();
    const innerDocFrom =
      lookback + tagOpen + 2 + (afterBrace.length - inner.length);

    // Condition keyword → condition sub-context
    const condMatch = inner.match(CONDITION_KEYWORDS);
    if (condMatch) {
      const conditionExpr = inner.slice(condMatch[0].length);
      const conditionExprDocFrom = innerDocFrom + condMatch[0].length;
      return {
        type: 'condition',
        conditionExpr,
        conditionExprDocFrom,
      };
    }

    return { type: 'tag', from: innerDocFrom };
  }

  // --- Output context: {{ ... }} ---
  const outputOpen = text.lastIndexOf('{{');
  const outputClose = text.lastIndexOf('}}');
  if (outputOpen !== -1 && outputOpen > outputClose) {
    const afterBrace = text.slice(outputOpen + 2);
    const inner = afterBrace.trimStart();
    const from = lookback + outputOpen + 2 + (afterBrace.length - inner.length);
    return { type: 'output', from };
  }

  return null;
};

export const liquidCompletionSource = context => {
  const ctx = detectLiquidContext(context.state.doc, context.pos);
  if (!ctx) return null;

  // --- Condition context ---
  if (ctx.type === 'condition') {
    const sub = getConditionSubContext(
      ctx.conditionExpr,
      ctx.conditionExprDocFrom
    );

    if (sub.sub === 'value') {
      // After an operator: offer enum values for system_stage, generic string hints otherwise
      let options = [];
      if (
        sub.varDef?.varType === 'enum' &&
        sub.varDef.label === 'event.system_stage'
      ) {
        options = SYSTEM_STAGE_VALUES.map(v => ({
          label: `"${v}"`,
          info: `system_stage = ${v}`,
          type: 'enum',
        }));
      }
      if (!options.length) return null;
      return { from: sub.from, options, validFor: /^["'\w]*$/ };
    }

    if (sub.sub === 'suffix') {
      return {
        from: sub.from,
        options: CONDITION_SUFFIXES.map(s => ({
          label: s.label,
          info: s.info,
          type: 'keyword',
        })),
        validFor: /^[!=.<> \w]*$/,
      };
    }

    // Default: variable path completion
    return {
      from: sub.from,
      options: CONDITION_VARIABLES.map(v => ({
        label: v.label,
        info: v.info,
        detail: v.varType,
        type: 'variable',
      })),
      validFor: /^[\w.]*$/,
    };
  }

  // --- Tag keyword context ---
  if (ctx.type === 'tag') {
    return {
      from: ctx.from,
      options: LIQUID_TAGS.map(t => ({
        label: t.label,
        detail: t.detail,
        info: t.info,
        apply: t.apply,
        type: 'keyword',
      })),
      validFor: /^[\w]*$/,
    };
  }

  // --- Output context ---
  if (ctx.type === 'output') {
    return {
      from: ctx.from,
      options: EVENT_VARIABLES.map(v => ({
        label: v.label,
        info: v.info,
        type: 'variable',
      })),
      validFor: /^[\w.]*$/,
    };
  }

  // --- Filter context ---
  if (ctx.type === 'filter') {
    return {
      from: ctx.from,
      options: LIQUID_FILTERS.map(f => ({
        label: f.label,
        detail: f.detail,
        info: f.info,
        type: 'function',
      })),
      validFor: /^[\w_]*$/,
    };
  }

  return null;
};

/** Indent Liquid block tags; return null so HTML can handle everything else. */
export const liquidIndentService = indentService.of((context, pos) => {
  const line = context.state.doc.lineAt(pos);
  const lineText = line.text;

  let prevFrom = null;
  let prevText = '';
  for (let n = line.number - 1; n >= 1; n -= 1) {
    const prev = context.state.doc.line(n);
    if (prev.text.trim()) {
      prevFrom = prev.from;
      prevText = prev.text;
      break;
    }
  }

  const currentDedents = LIQUID_DEDENT_RE.test(lineText);
  const prevOpens = prevFrom != null && LIQUID_OPEN_RE.test(prevText);
  const currentOpens = LIQUID_OPEN_RE.test(lineText);

  if (!prevOpens && !currentDedents && !currentOpens) {
    return null;
  }

  const unit = context.unit;
  let indent = prevFrom == null ? 0 : context.lineIndent(prevFrom);
  if (prevOpens) indent += unit;
  if (currentDedents) indent = Math.max(0, indent - unit);
  return indent;
});
